import isBoolean from "lodash/isBoolean";
import keyBy from "lodash/keyBy";
import merge from "lodash/merge";
import toString from "lodash/toString";
import values from "lodash/values";
import { utc } from "PFCore/helpers/date";
import { useBooleanToString } from "PFCore/hooks/use_boolean_to_string";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { ActivityPureCustomValue, CustomValue, MatchCustomValue, PureCustomValue } from "PFTypes";
import { CustomType } from "PFTypes/custom_type";
import { useCallback } from "react";

import { requiresApproval as typeRequiresApproval, requiresCuration } from "./custom_type";

export const useCustomValueToString = () => {
  const { formatDate } = useDateFormatter();
  const booleanToString = useBooleanToString();
  const getCustomValueString = useCallback(
    (customValue: Pick<PureCustomValue, "text" | "value">, valueType?: CustomType["value_type"]): string => {
      const { value, text } = customValue;
      if (value && valueType === "date") {
        return formatDate(utc(value as string));
      }
      if (isBoolean(text)) {
        return booleanToString(text);
      }

      return toString(value || text);
    },
    [formatDate, booleanToString]
  );

  const getCustomValuesListString = useCallback(
    // For list endpoints (e.g. api/matches/index) API limits Custom Values to 10.
    // For some others (e.g. api/profiles/:id) - we receive all Custom Values, so we limit them in the UI
    (customValues: CustomValue[], valueType: CustomType["value_type"], limit = 10) =>
      customValues
        .slice(0, limit)
        .map((customValue) => getCustomValueString(customValue, valueType))
        .join(", "),
    [getCustomValueString]
  );

  return { getCustomValueString, getCustomValuesListString };
};

export const requiresApproval = (
  type: CustomType,
  customValue: Partial<Pick<CustomValue, "globalId" | "global_id" | "state">>
): boolean => {
  const ctRequiresAdminApproval = typeRequiresApproval(type);
  const ctRequiresCuration = requiresCuration(type);
  const globalId = customValue.globalId ?? customValue.global_id;
  const notApproved = !!customValue.state && customValue.state !== "approved";

  if (ctRequiresAdminApproval && ctRequiresCuration) {
    return !globalId && notApproved;
  }

  return ctRequiresAdminApproval && notApproved;
};

export const generateMatchCustomValues = (
  profileValues: MatchCustomValue[],
  activityValues: ActivityPureCustomValue[]
) =>
  values<MatchCustomValue | ActivityPureCustomValue>(
    merge(keyBy(profileValues, "id"), keyBy(activityValues, "id"))
  );
